import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Box, Button, FormControlLabel, Menu, MenuItem, Radio, RadioGroup, Typography } from '@mui/material';
import { map } from 'es-toolkit/compat';
import React from 'react';

import { CaseOrganizationExecutionMode, caseOrganizationHandlingLabels } from './caseOrganizationPlan';
import { ManifestSurface } from './dialogState';

export type ManifestWorkspaceSurface = ManifestSurface | 'Cross Lab Search';

interface CaseManifestSlideSourceMenuProps {
  anchorEl: HTMLElement | null;
  disabled?: boolean;
  entries: { id: ManifestWorkspaceSurface; label: string }[];
  isChoosingEntryIntent: boolean;
  label: string;
  onChange: (surface: ManifestWorkspaceSurface) => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  selectedSurface: ManifestWorkspaceSurface;
  setAnchorEl: (element: HTMLButtonElement | null) => void;
}

export const CaseManifestSlideSourceMenu = ({
  anchorEl,
  disabled = false,
  entries,
  isChoosingEntryIntent,
  label,
  onChange,
  onOpenChange,
  open,
  selectedSurface,
  setAnchorEl,
}: CaseManifestSlideSourceMenuProps) => {
  const singleEntry = entries.length === 1 ? entries[0] : null;
  if (singleEntry) {
    return (
      <Button
        variant={isChoosingEntryIntent ? 'contained' : 'outlined'}
        color={isChoosingEntryIntent ? 'primary' : 'inherit'}
        disabled={disabled}
        onClick={() => onChange(singleEntry.id)}
      >
        {singleEntry.label}
      </Button>
    );
  }
  return (
    <>
      <Button
        ref={setAnchorEl}
        variant={isChoosingEntryIntent ? 'contained' : 'outlined'}
        color={isChoosingEntryIntent ? 'primary' : 'inherit'}
        endIcon={<ArrowDropDownIcon />}
        onClick={() => onOpenChange(true)}
        disabled={disabled || entries.length === 0}
        aria-controls={open ? 'select-slides-menu' : undefined}
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : undefined}
      >
        {isChoosingEntryIntent ? 'Assign more slides to the study' : label}
      </Button>
      <Menu
        id="select-slides-menu"
        anchorEl={anchorEl}
        open={open && Boolean(anchorEl)}
        onClose={() => onOpenChange(false)}
      >
        {map(entries, (entry) => (
          <MenuItem key={entry.id} selected={entry.id === selectedSurface} onClick={() => onChange(entry.id)}>
            {entry.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

interface CaseManifestHandlingProps {
  disabled: boolean;
  executionMode: CaseOrganizationExecutionMode;
  isExecutionModeValid: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const selectedHandlingHelp: Partial<Record<CaseOrganizationExecutionMode, string>> = {
  routine: 'Other slides stay in their current cases.',
  selectedCaseOverride: 'Listed cases keep only the slides shown here.',
  wholeStudyReplacement: 'Cases not listed here are deleted from this study.',
};

export const CaseManifestHandling = ({
  disabled,
  executionMode,
  isExecutionModeValid,
  onChange,
}: CaseManifestHandlingProps) => (
  <Box sx={{ px: 2, py: 1.5 }}>
    <RadioGroup
      aria-label="Existing case handling"
      value={isExecutionModeValid ? executionMode : ''}
      onChange={onChange}
    >
      {map(caseOrganizationHandlingLabels, (label, mode) => (
        <FormControlLabel key={mode} value={mode} control={<Radio />} label={label} disabled={disabled} />
      ))}
    </RadioGroup>
    {isExecutionModeValid && selectedHandlingHelp[executionMode] ? (
      <Typography variant="body2" color="text.secondary">
        {selectedHandlingHelp[executionMode]}
      </Typography>
    ) : null}
  </Box>
);

interface CaseManifestEntryChoicesProps {
  canReviewUnassigned: boolean;
  caseListLoadingIcon: React.ReactNode;
  canReviewExisting: boolean;
  isCasesError: boolean;
  isCasesLoading: boolean;
  ingestNotice?: React.ReactNode;
  manifestFileControl: React.ReactNode;
  onReviewExisting: () => void;
  onReviewUnassigned: () => void;
  selectSlidesControl: React.ReactNode;
  studySourceStatus: 'loading' | 'error' | null;
  unassignedStudySlideCount: number;
}

export const CaseManifestEntryChoices = ({
  canReviewUnassigned,
  caseListLoadingIcon,
  canReviewExisting,
  isCasesError,
  isCasesLoading,
  ingestNotice,
  manifestFileControl,
  onReviewExisting,
  onReviewUnassigned,
  selectSlidesControl,
  studySourceStatus,
  unassignedStudySlideCount,
}: CaseManifestEntryChoicesProps) => (
  <Box>
    {ingestNotice && <Box sx={{ mb: 2 }}>{ingestNotice}</Box>}
    <Box sx={{ display: 'flex', gap: 2, '& > .MuiButton-root': { flex: 1, minWidth: 0, minHeight: 44 } }}>
      {canReviewUnassigned && (
        <Button color="primary" variant="contained" onClick={onReviewUnassigned}>
          Review {unassignedStudySlideCount} slides awaiting assignment
        </Button>
      )}
      {canReviewExisting && (
        <Button
          color="primary"
          variant="contained"
          aria-busy={isCasesLoading}
          startIcon={caseListLoadingIcon}
          onClick={onReviewExisting}
        >
          Review existing case assignments
        </Button>
      )}
      {selectSlidesControl}
    </Box>
    {manifestFileControl ? <Box sx={{ mt: 1.5 }}>{manifestFileControl}</Box> : null}
    {studySourceStatus && (
      <Typography role="status" variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        {studySourceStatus === 'loading'
          ? 'Loading all slides awaiting assignment...'
          : 'Could not load all slides awaiting assignment. Please contact support.'}
      </Typography>
    )}
    {isCasesError && (
      <Typography role="status" variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Could not load all study cases. Please contact support.
      </Typography>
    )}
  </Box>
);
